/**
 * Badge and chart colours per status, defined once.
 *
 * These were duplicated across three screens, and the copies had already
 * drifted: the Company Report mapped a new ticket to `b-new`, which is not one
 * of the prototype's five badge classes, so those badges rendered unstyled while
 * the Support list showed them correctly.
 *
 * The values are the prototype's own — its `compBadge` maps `new` and `assigned`
 * onto the same review/progress classes rather than inventing a sixth colour.
 */

/** The five badge classes the vendored stylesheet actually defines. */
export type BadgeClass = 'b-review' | 'b-progress' | 'b-done' | 'b-reject' | 'b-neutral';

export const TICKET_BADGE: Record<string, BadgeClass> = {
  new: 'b-review',
  assigned: 'b-progress',
  in_progress: 'b-progress',
  resolved: 'b-done',
  rejected: 'b-reject',
};

export const EMPLOYEE_BADGE: Record<string, BadgeClass> = {
  active: 'b-done',
  invited: 'b-review',
  resigned: 'b-neutral',
};

export const ROOM_BADGE: Record<string, BadgeClass> = {
  available: 'b-done',
  maintenance: 'b-review',
  closed: 'b-neutral',
};

export const INVITEE_BADGE: Record<string, BadgeClass> = {
  invited: 'b-review',
  accepted: 'b-done',
  declined: 'b-neutral',
};

/**
 * FEAT-018 / FEAT-023 — the Administration screen's states.
 *
 * Same five classes as everywhere else. A sixth colour for "connected" would
 * have to be added to the vendored stylesheet, which ADR 0004 forbids, and it
 * would say the same thing green already says on four other screens.
 */
export const CONNECTION_BADGE: Record<string, BadgeClass> = {
  connected: 'b-done',
  needs_reconnect: 'b-review',
  disconnected: 'b-neutral',
  is_set: 'b-done',
  not_set: 'b-neutral',
  overridden: 'b-progress',
  default: 'b-neutral',
};

export function badgeFor(map: Record<string, BadgeClass>, status: string): string {
  return `badge ${map[status] ?? 'b-neutral'}`;
}

/**
 * Chart colours, for the company report's donut and bars.
 *
 * Custom properties rather than literals, so both themes are covered by the
 * token definitions that already exist. There is no `--s-new`, which is why
 * `new` borrows the review colour here exactly as its badge does.
 */
export const EMPLOYEE_COLOUR: Record<string, string> = {
  active: 'var(--s-done)',
  invited: 'var(--s-review)',
  resigned: 'var(--s-reject)',
};

export const TICKET_COLOUR: Record<string, string> = {
  new: 'var(--s-review)',
  assigned: 'var(--s-progress)',
  in_progress: 'var(--s-progress)',
  resolved: 'var(--s-done)',
  rejected: 'var(--s-reject)',
};
